import React, { useState, useEffect } from 'react';
import { Box, Typography, Modal, IconButton } from '@mui/material';
import {  
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Treemap, ResponsiveContainer } from 'recharts';
import { EmotionTreemapData, ThemeCorrelation } from './types/emotionThemeTypes';
import { serenityColors } from '../../utils/serenityColors';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface EmotionThemeTreemapProps {
  data: EmotionTreemapData[];
  period: number;
}

const EmotionThemeTreemap: React.FC<EmotionThemeTreemapProps> = ({ data }) => {
  const [selectedItem, setSelectedItem] = useState<ThemeCorrelation | null>(null);
  const [modalOpen, setModalOpen] = useState(false);


  // Calculer la couleur en fonction de l'intensité
  const getColor = (intensity: number) => {
    const colorStops = [
      { threshold: 0.05, color: 'var(--treemap-color-0)' },
      { threshold: 0.1, color: 'var(--treemap-color-5)' },
      { threshold: 0.15, color: 'var(--treemap-color-10)' },
      { threshold: 0.2, color: 'var(--treemap-color-15)' },
      { threshold: 0.25, color: 'var(--treemap-color-20)' },
      { threshold: 0.3, color: 'var(--treemap-color-25)' },
      { threshold: 0.35, color: 'var(--treemap-color-30)' },
      { threshold: 0.4, color: 'var(--treemap-color-35)' },
      { threshold: 0.45, color: 'var(--treemap-color-40)' },
      { threshold: 0.5, color: 'var(--treemap-color-45)' },
      { threshold: 0.55, color: 'var(--treemap-color-50)' },
      { threshold: 0.6, color: 'var(--treemap-color-55)' },
      { threshold: 0.65, color: 'var(--treemap-color-60)' },
      { threshold: 0.7, color: 'var(--treemap-color-65)' },
      { threshold: 0.75, color: 'var(--treemap-color-70)' },
      { threshold: 0.8, color: 'var(--treemap-color-75)' },
      { threshold: 0.85, color: 'var(--treemap-color-80)' },
      { threshold: 0.9, color: 'var(--treemap-color-85)' },
      { threshold: 0.95, color: 'var(--treemap-color-90)' },
      { threshold: 1.0, color: 'var(--treemap-color-95)' }
    ];
  
    // Trouver le colorStop approprié
  let selectedStop = colorStops[colorStops.length - 1];
  for (const stop of colorStops) {
    if (intensity <= stop.threshold) {
      selectedStop = stop;
      break;
    }
  }

  // Opacité aléatoire entre 60% et 100%
  const opacity = 0.2 + (Math.random() * 0.8);
  const opacityHex = Math.round(opacity * 255).toString(16).padStart(2, '0');

  // Résoudre la couleur CSS
  const varName = selectedStop.color.replace('var(', '').replace(')', '');
  const resolvedColor = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();

  return `${resolvedColor}${opacityHex}`;
};

  // Customisation du contenu des rectangles
  const CustomizedContent = (props: any) => {
    const { x, y, width, height, theme, value } = props;
    const intensity = value || 0;
    const displayValue = Math.round(value * 100);
    //if (width > 1000 || !theme) {
    //  return null;
    //}
    if (width > 1000 || !theme) {
      return (
        <g>
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill={getColor(intensity)}
            stroke="rgba(255, 255, 255, 0.3)"
            strokeWidth={0}
            rx={0}
            ry={0}
          />
        </g>
      );
    }
    
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={getColor(intensity)}
          stroke="rgba(0, 255, 255, 0.93)"
          strokeWidth={0}
          rx={10}
          ry={10}
        />
        {width > 30 && height > 30 && (
          <text
            x={x + width / 2}
            y={y + height / 2}
            textAnchor="middle"
            fill="black"
            fontSize={12}
          >
            <tspan x={x + width / 2} dy="-0.5em">{theme}</tspan>
            <tspan x={x + width / 2} dy="1.2em">{displayValue}%</tspan>
          </text>
        )}
      </g>
    );
  };

  // Modal de détails
  const DetailModal = () => (
    <Modal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      aria-labelledby="correlation-details"
    >
     <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 300,
        background: 'var(--stat-progress)',
        borderRadius: 0.5,
        boxShadow: 24,
        p: 4,
        color: 'black', // Couleur de texte par défaut pour tout le contenu de la Box
        }}>

            <IconButton
              onClick={() => setModalOpen(false)}
              size="small"
              sx={{
                position: 'absolute',
                right: 10,
                top: 10,
                color: 'white',
                '&:hover': {
                  color: '#68f1d1',
                  backgroundColor: '#1A1B35',
                  },
              }}
                        >
              <CloseIcon fontSize="medium" />
            </IconButton>

          {selectedItem && (
        <>
            <Typography variant="h5" component="h2" gutterBottom sx={{ color: 'white' }}>
                {selectedItem.theme}
            </Typography>
            <Typography sx={{ mt: 1, color: 'white' }}>
                Score de corrélation : {Math.round(selectedItem.details.correlationScore * 100)}%
            </Typography>
            <Typography sx={{ mt: 0, color: 'black' }}>
                Nombre total d'occurrences : {selectedItem.details.totalOccurrences}
            </Typography>
            <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
                Rêves récents associés :
            </Typography>
              <Box sx={{ mt: -1 }}>
                  {selectedItem.details.dreams.map((dream) => (
                      <Typography key={dream.id} sx={{ mt: 1, fontSize: '0.9rem', color: 'black' }}>
                          {format(new Date(dream.date), 'dd MMM yyyy', { locale: fr })} - {dream.title}
                      </Typography>
                  ))}
              </Box>
        </>
          )}
      </Box>
    </Modal>
  );

  if (!data || data.length === 0) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px' 
      }}>
        <Typography color="text.secondary">
          Pas assez de données pour afficher les corrélations
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      p: 4,
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: 2, 
      minHeight: '300px', 
      width: '100%',  // Forcer la largeur totale
      overflow: 'hidden'  // Empêcher tout débordement
    }}>
      {data.map((emotionData) => (
        <Box 
          key={emotionData.emotion} 
          sx={{ 
            flex: 1,
            minWidth: 0,  // Permettre la réduction
            maxWidth: '100%',  // Largeur maximale
            display: 'flex',
            flexDirection: 'column'
          }}
        >          
          <Box sx={{ 
            flex: 1, 
            minHeight: '250px',  // Hauteur minimale fixe
            width: '100%', 
            overflow: 'hidden' 
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <Treemap
                data={[emotionData]}
                dataKey="value"
                content={<CustomizedContent />}
                onClick={(data: any) => {
                  if (data && data.theme) {
                    setSelectedItem(data);
                    setModalOpen(true);
                  }
                }}
              />
            </ResponsiveContainer>
          </Box>
        </Box>
      ))}
      <DetailModal />
    </Box>
  );
};

export default EmotionThemeTreemap;
